import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  useTheme,
  styled,
} from '@mui/material';
import { ArrowForward, LocationOn, Star, Hotel } from '@mui/icons-material';
import DashboardHeader from '../common/DashboardHeader';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import {
  DASHBOARD_ACCENT,
  HOTELIER_HERO_HEIGHT,
  getHotelierCardSx,
  getHotelierCardContentSx,
  getHotelierEmptyPanelSx,
  getHotelierFloatingIconSx,
  getHotelierHeroOverlaySx,
  getHotelierImagePlaceholderSx,
  getHotelierLocationBarSx,
  getHotelierLocationIconSx,
  getHotelierMetaChipSx,
  getHotelierOpenRowSx,
  getHotelierRatingPillSx,
  getHotelierRenewLinkSx,
  getHotelierStatusChipSx,
  getHotelierSurfaceTokens,
} from '../../utils/hotelierCardLayout';

const StyledCardActionArea = styled(CardActionArea)({
  '&:hover': {
    backgroundColor: 'transparent !important',
    '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
  },
  '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
});

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const getDaysUntilExpiry = (expiryValue) => {
  if (!expiryValue) return null;
  const expiryDate = new Date(expiryValue);
  if (Number.isNaN(expiryDate.getTime())) return null;
  return Math.ceil((expiryDate.getTime() - Date.now()) / MS_IN_DAY);
};

const getTopStatus = (hotel) => {
  const isSubscriptionExpired = hotel.subscription_status === 'expired' || hotel.subscription_is_expired === true;
  if (isSubscriptionExpired) return 'subscription_expired';
  if (hotel.status === 'active') return 'active';
  if (hotel.status === 'onboarding') return 'onboarding';
  return 'suspended';
};

const statusLabel = (status) => {
  if (status === 'subscription_expired') return 'Expired';
  if (status === 'active') return 'Active';
  if (status === 'onboarding') return 'Onboarding';
  return 'Suspended';
};

const LandingHotels = React.memo(function LandingHotels({
  hotels = [],
  loading = false,
  error = null,
  onHotelClick,
  canManageSubscription = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tokens = getHotelierSurfaceTokens(isDark);
  const [imageErrors, setImageErrors] = React.useState(new Set());

  const handleSelectHotel = React.useCallback((hotelId) => {
    onHotelClick?.(hotelId);
  }, [onHotelClick]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Hotelier"
          forceDark={isMobileShellLayout()}
          breadcrumbs={[
            { label: 'Home', path: '/onboarding/landing/' },
            { label: 'Hotelier' },
          ]}
          navLinks={[
            { url: '/onboarding/landing/?view=dashboard', label: 'Dashboard' },
          ]}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress size={40} sx={{ color: DASHBOARD_ACCENT }} />
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={getHotelierEmptyPanelSx(isDark)}>
          <Typography color="error" sx={{ fontSize: '14px' }}>{error}</Typography>
        </Paper>
      ) : hotels && Array.isArray(hotels) && hotels.length > 0 ? (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {hotels.map((hotel, index) => {
            const isSubscriptionExpired = hotel.subscription_status === 'expired' || hotel.subscription_is_expired === true;
            const daysUntilExpiry = getDaysUntilExpiry(hotel.subscription_expires_at);
            const isExpiringSoon =
              hotel.subscription_status === 'active'
              && typeof daysUntilExpiry === 'number'
              && daysUntilExpiry >= 0
              && daysUntilExpiry < 30;
            const shouldShowRenewLink = isSubscriptionExpired || isExpiringSoon;
            const topStatus = getTopStatus(hotel);
            const hasImage = hotel.primary_image_url && !imageErrors.has(hotel.id);

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={hotel.id}
                className="rn-hotel-card-enter"
                style={{ '--hotel-card-delay': `${Math.min(index, 8) * 70}ms` }}
              >
                <Card
                  elevation={0}
                  sx={getHotelierCardSx(isDark, { disabled: isSubscriptionExpired })}
                  onClick={() => {
                    if (!isSubscriptionExpired) handleSelectHotel(hotel.id);
                  }}
                >
                  <StyledCardActionArea sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 0 }}>
                    <Box sx={{ position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                      {hasImage ? (
                        <CardMedia
                          component="img"
                          image={hotel.primary_image_url}
                          alt={hotel.name || 'Hotel'}
                          className="hotelier-hero-img"
                          sx={{
                            objectFit: 'cover',
                            backgroundColor: isDark ? tokens.surface : '#f5f5f5',
                            height: HOTELIER_HERO_HEIGHT,
                            minHeight: HOTELIER_HERO_HEIGHT,
                            width: '100%',
                            transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
                            display: 'block',
                          }}
                          onError={() => {
                            setImageErrors((prev) => new Set([...prev, hotel.id]));
                          }}
                        />
                      ) : (
                        <Box sx={getHotelierImagePlaceholderSx(isDark)}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, zIndex: 1 }}>
                            <Hotel sx={{ fontSize: 40, color: DASHBOARD_ACCENT, opacity: 0.85 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: tokens.subtleText,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontSize: '0.65rem',
                                textAlign: 'center',
                                px: 2,
                              }}
                            >
                              No photo
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box className="hotelier-hero-overlay" sx={getHotelierHeroOverlaySx(isDark)} />

                      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 3 }}>
                        <Chip
                          label={statusLabel(topStatus)}
                          size="small"
                          sx={getHotelierStatusChipSx(topStatus === 'subscription_expired' ? 'suspended' : topStatus)}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={getHotelierCardContentSx()}>
                      <Box className="hotelier-floating-icon" sx={getHotelierFloatingIconSx(isDark)}>
                        <Hotel sx={{ fontSize: 26 }} />
                      </Box>

                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.05rem',
                          lineHeight: 1.35,
                          letterSpacing: '-0.01em',
                          color: tokens.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          pr: 0.5,
                        }}
                      >
                        {hotel.name || 'Unnamed Hotel'}
                      </Typography>

                      {canManageSubscription && shouldShowRenewLink ? (
                        <Box
                          component="a"
                          href="/onboarding/landing/?view=settings&tab=subscription"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            window.location.href = '/onboarding/landing/?view=settings&tab=subscription';
                          }}
                          sx={getHotelierRenewLinkSx(isDark)}
                        >
                          {isSubscriptionExpired
                            ? 'Subscription expired — Renew now'
                            : `Expires in ${daysUntilExpiry === 0 ? '<1' : daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'} — Renew now`}
                        </Box>
                      ) : null}

                      {(hotel.property_type_display || hotel.star_rating_display) ? (
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.85} sx={{ mt: 1.35 }}>
                          {hotel.property_type_display ? (
                            <Chip label={hotel.property_type_display} size="small" sx={getHotelierMetaChipSx(isDark)} />
                          ) : null}
                          {hotel.star_rating_display ? (
                            <Box sx={getHotelierRatingPillSx(isDark)}>
                              <Star sx={{ fontSize: 15, color: '#f6da6e' }} />
                              {hotel.star_rating_display}
                            </Box>
                          ) : null}
                        </Stack>
                      ) : null}

                      {(hotel.city || hotel.country) ? (
                        <Box sx={getHotelierLocationBarSx(isDark)}>
                          <Box sx={getHotelierLocationIconSx(isDark)}>
                            <LocationOn sx={{ fontSize: 18 }} />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.84rem',
                              fontWeight: 600,
                              color: tokens.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                          </Typography>
                        </Box>
                      ) : null}

                      {!isSubscriptionExpired ? (
                        <Box className="hotelier-open-row" sx={getHotelierOpenRowSx(isDark)}>
                          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                            Open workspace
                          </Typography>
                          <ArrowForward sx={{ fontSize: 16 }} />
                        </Box>
                      ) : null}
                    </CardContent>
                  </StyledCardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper elevation={0} sx={{ ...getHotelierEmptyPanelSx(isDark), textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', color: tokens.subtleText }}>
            {hotels && Array.isArray(hotels) && hotels.length === 0
              ? 'No hotels found. Please contact your administrator to assign hotels to your account.'
              : 'Loading hotels...'}
          </Typography>
        </Paper>
      )}

    </Box>
  );
});

export default LandingHotels;
